/**
 * 推送项目配置（项目定制层）
 * 本项目所有与推送相关的业务差异（路由映射、点击行为、状态同步、cid 上报）都在这里配置，
 * 通用逻辑见 @/plugins/uni-push（可整体复制到其他项目复用）
 */
import type { UniPushOptions } from '@/plugins/uni-push';
import { usePushStore } from '@/store';
import { onReceive, receiveHandlers } from './handlers';

export const pushConfig: UniPushOptions = {
  // 兜底首页（点击消息无法解析路由时跳转）
  homePage: '/pages/tabbar/home/home',

  /**
   * 点击通知 -> 跳转路径映射（路径需与 pages.json 实际路由一致）
   * 返回空字符串走 homePage 兜底
   */
  resolveRoute: (data) => {
    switch (data.type) {
      case 'order':
        return data.id ? `/pages/order/details/index?id=${data.id}` : '/pages/order/list/list';
      case 'chat':
        return data.id ? `/pages-chat/operator/index?id=${data.id}` : '/pages-chat/workstation/index';
      case 'web':
        return data.url ? `/pages/common/webview?url=${encodeURIComponent(data.url)}` : '';
      default:
        return ''; // 未知类型：返回空，由插件兜底跳首页
    }
  },

  /**
   * 点击消息的附加处理（返回 undefined 不接管，插件继续执行默认跳转）
   */
  onClick: (data) => {
    // 点击后清空对应类型未读
    if (data?.type) usePushStore().clearUnreadCount(data.type);
    // 可选：上报点击统计
    // trackEvent('push_click', { type: data?.type, id: data?.id });
  },

  // 前台透传消息按 payload.type 分发处理（见 ./handlers.ts）
  receiveHandlers,
  onReceive,

  /**
   * 通知提醒（声音/震动）：收到 receive 消息时按 payload 的
   * sound/customSound/forceSound/vibrate 字段提醒，设置开关从 push store 读取（消息通知设置页维护）
   */
  alert: {
    getSettings: () => {
      const store = usePushStore();
      return {
        soundEnabled: store.pushSoundEnabled,
        vibrateEnabled: store.pushVibrateEnabled,
      };
    },
  },

  /**
   * 获取到 clientId 后回调
   * cid 已在登录/注册/退出接口中自动附带（见 @/api/login，通过 getCachedPushCid 注入）；
   * 如需处理“已登录状态下 cid 变化”（卸载重装/清缓存后冷启动，不会再走登录接口），
   * 可在这里调后端刷新绑定接口
   */
  onClientId: (cid) => {
    console.log('[Push Config] clientId 就绪:', cid);
    // 可选：已登录时刷新 cid 绑定
    // if (useTokenStore().hasLogin) refreshPushCid({ cid });
  },

  // 收到任意消息时记录最后一条到 store（调试/页面展示用）
  onMessage: (msg, type) => {
    usePushStore().setLastMessage(msg, type);
  },

  // 插件运行时状态同步到 pinia（clientId / 初始化状态 / 监听状态）
  onStateChange: (state) => {
    usePushStore().syncPluginState(state);
  },
};
